<template>
  <div>
    <apply-template-dialog
      :message-type="applyTemplateMessageId"
      v-if="applyTemplateMessageId"
      :show-confirmation="showConfirmation"
      :current-message="currentMessage"
    />
    <copy-from-dialog
      v-if="copyFromMessageId"
      :message-type="copyFromMessageId"
      :current-message="currentMessage"
      :model="value"
    />
    <component
      v-if="currentMessage"
      :is="component"
      v-model="currentMessage"
      :model="value"
      :key="key"
      :title="title"
      v-bind="bindValues"
      v-loading="loading"
    >
      <template v-slot:selected-template-info>
        <selected-template-alert v-model="currentMessage" :model="value" />
      </template>
    </component>
  </div>
</template>

<script>
import AndroidPush from '@/modules/campaign-manage/components/content/channels/AndroidPush'
import IosPush from '@/modules/campaign-manage/components/content/channels/IosPush'
import WebPush from '@/modules/campaign-manage/components/content/channels/WebPush'
import Email from '@/modules/campaign-manage/components/content/channels/Email'
import SMS from '@/modules/campaign-manage/components/content/channels/SMS'
import Inbox from '@/modules/campaign-manage/components/content/channels/Inbox'
import InApp from '@/modules/campaign-manage/components/content/channels/InApp'
import OnSite from '@/modules/campaign-manage/components/content/channels/OnSite'
import Facebook from '@/modules/campaign-manage/components/content/channels/Facebook'
import Whatsapp from '@/modules/campaign-manage/components/content/channels/Whatsapp'
import Webhook from '@/modules/campaign-manage/components/content/channels/Webhook'
import Workflow from '@/modules/campaign-manage/components/content/channels/Workflow'
import ApplyTemplateDialog from '@/modules/campaign-manage/components/ApplyTemplateDialog'
import SelectedTemplateAlert from '@/modules/campaign-manage/components/SelectedTemplateAlert'
import { EventBus } from '@/core/EventBus'
import {
  channelTypes,
  channelLabels
} from '@/modules/campaign-manage/enum/Labels'
import _ from 'lodash'
import { languages as languagesList } from '@/modules/campaign-manage/enum/LanguagesEnum'
import { defaultMessages } from '@/modules/campaign-manage/enum/DefaultMessages'
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { inAppTypes } from '@/modules/campaign-manage/enum/InAppTypesEnum'
import CopyFromDialog from '@/modules/campaign-manage/components/CopyFromDialog'
export default {
  name: 'CampaignContentPage',
  components: { CopyFromDialog, ApplyTemplateDialog, SelectedTemplateAlert },
  mounted() {
    EventBus.$on('applyMessageTemplateDialog', id => {
      this.showConfirmation = !_.isEqualWith(
        this.currentMessage,
        defaultMessages[this.channel],
        (val1, val2, key) =>
          this.ignoredFields.includes(key) ? true : undefined
      )
      this.applyTemplateMessageId = id
    })
    EventBus.$on('applyMessageTemplate', data => {
      this.applyMessageTemplate(data)
    })

    EventBus.$on('copyFromDialog', id => {
      this.copyFromMessageId = id
    })

    EventBus.$on('applyCopyMessage', data => {
      const dataToCopy = _.cloneDeep(data.content)
      const fieldsToRemove = ['id', 'variant_key', 'language', 'message_type']
      fieldsToRemove.forEach(f => {
        if (dataToCopy.hasOwnProperty(f)) {
          delete dataToCopy[f]
        }
      })

      let messages = this.value.messages
      let msg = messages[this.channel][this.variant]['content']
      if (data.lang) {
        msg = messages[this.channel][this.variant]['languages'][this.lang]
      }

      if (this.channel === channelTypes.inbox && data.channel === 'web_push') {
        this.webPushToInboxCopyFields.forEach(field => {
          this.$set(msg, field, dataToCopy[field])
        })
      } else {
        if (data.lang) {
          this.$set(
            this.value.messages[this.channel][this.variant]['languages'],
            this.lang,
            {
              ...msg,
              ...dataToCopy
            }
          )
        } else {
          this.$set(
            this.value.messages[this.channel][this.variant],
            'content',
            {
              ...msg,
              ...dataToCopy
            }
          )
        }
      }
      if (this.channel === channelTypes.inbox) {
        EventBus.$emit('applyPushTemplate-' + this.key, data.message)
      }
      this.copyFromMessageId = null
    })

    EventBus.$on('generateTitle', () => {
      EventBus.$emit('fetchingData', true)
      let emailBodyInPlainText = ''
      if (this.currentMessage.email_template) {
        emailBodyInPlainText = this.extractText(
          this.currentMessage.email_template
        )
      } else if (this.currentMessage.email_html !== '') {
        emailBodyInPlainText = this.extractTextFromHtml(
          this.currentMessage.email_html
        )
      }

      if (emailBodyInPlainText === '') {
        this.dataFetchStopped()
        this.$message({
          message: this.$t('Campaign_Generate_Subject_No_Body_Error'),
          type: 'error'
        })
        return
      }

      RestApi(
        projectEndpoint({
          method: 'post',
          url: `generate/subject-line`,
          data: {
            type: 'email',
            param: emailBodyInPlainText
          }
        })
      )
        .then(({ data }) => {
          this.dataFetchStopped()
          this.currentMessage.email_subject = data.result.trim()
        })
        .catch(error => {
          this.dataFetchStopped()
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    })

    if (this.$route.query.channel === 'sms') {
      this.fetchOptOutAndShortenUrlData()
    }
  },
  destroyed() {
    EventBus.$off('applyMessageTemplateDialog')
    EventBus.$off('applyMessageTemplate')
    EventBus.$off('applyCopyMessage')
    EventBus.$off('copyFromDialog')
    EventBus.$off('generateTitle')
  },
  data() {
    return {
      showConfirmation: false,
      applyTemplateMessageId: null,
      copyFromMessageId: null,
      optOutSettings: {
        enabled: false
      },
      shortenUrl: false,
      shortenUrlDomain: null,
      languagesList,
      channelLabels,
      ignoredFields: [
        'email_subject',
        'email_from_address',
        'email_from_name',
        'email_reply_to_address',
        'email_reply_to_name'
      ],
      webPushToInboxCopyFields: [
        'push_title',
        'push_text',
        'push_icon',
        'push_action'
      ],
      loading: false
    }
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },

  methods: {
    applyInapp(data) {
      this.currentMessage.inapp_type = data.message.inapp_type
      this.currentMessage.inapp_orientation =
        data.message.inapp_orientation || 0

      if (data.message.inapp.hasOwnProperty('native')) {
        this.$set(
          this.currentMessage.inapp,
          'native',
          data.message.inapp.native
        )
        // Set Template
        if (this.currentMessage.inapp.native.payload.form) {
          this.$set(
            this.currentMessage.inapp.native.payload,
            'formTemplate',
            this.currentMessage.inapp.native.payload.form
          )
        }
        // Set empty Form Values
        if (
          !Object.keys(this.currentMessage.inapp.native.payload.formValues)
            .length
        ) {
          this.currentMessage.inapp.native.payload.formValues = {}
          Object.keys(
            this.currentMessage.inapp.native.payload.formSettings
          ).forEach(key => {
            this.$set(
              this.currentMessage.inapp.native.payload.formValues,
              key,
              null
            )
          })
        }
      }

      if (data.message.inapp.hasOwnProperty('buttons')) {
        this.$set(
          this.currentMessage.inapp,
          'buttons',
          data.message.inapp.buttons
        )
      }
      if (data.message.inapp.hasOwnProperty('editor')) {
        this.$set(
          this.currentMessage.inapp,
          'editor',
          data.message.inapp.editor
        )
      }
      if (data.message.inapp.hasOwnProperty('placeholder')) {
        this.$set(this.currentMessage.inapp, 'placeholder', {
          ...this.currentMessage.inapp.placeholder,
          ...data.message.inapp.placeholder
        })
      }

      if (data.message.inapp.hasOwnProperty('preview')) {
        this.$set(
          this.currentMessage.inapp,
          'preview',
          data.message.inapp.preview
        )
      }

      if (data.message.inapp.hasOwnProperty('creatives')) {
        this.currentMessage.inapp.creatives.portrait = Object.values(
          data.message.inapp.creatives.portrait
        )
        this.currentMessage.inapp.creatives.landscape = Object.values(
          data.message.inapp.creatives.landscape
        )
      }

      if (data.message.inapp_type === inAppTypes.HTML) {
        this.$set(this.currentMessage.inapp, 'html', data.message.inapp.html)
      }
    },
    applyMessageTemplate(data) {
      switch (this.channel) {
        case channelTypes.email: {
          this.currentMessage.email_html = data.message.email_html
          this.currentMessage.email_template = data.message.email_template
          break
        }
        case channelTypes.workflow: {
          this.currentMessage.workflow_id = data.message.workflow_id
          break
        }
        case channelTypes.inbox: {
          this.currentMessage.push_text = data.message.push_text
          this.currentMessage.push_title = data.message.push_title
          this.currentMessage.inbox_style = data.message.inbox_style
          this.currentMessage.inbox_type = data.message.inbox_type
          this.currentMessage.payload_add = data.message.payload_add
          this.currentMessage.push_action = data.message.push_action
          if (data.message.push_action.hasOwnProperty('url_blank')) {
            this.currentMessage.push_action.url_blank = parseInt(
              data.message.push_action.url_blank
            )
          }
          this.currentMessage.push_buttons = data.message.push_buttons
          this.currentMessage.push_picture = data.message.push_picture
          this.currentMessage.push_icon = data.message.push_icon
          this.currentMessage.push_picture_type = data.message.push_picture_type
          EventBus.$emit('applyPushTemplate-' + this.key, data.message)
          break
        }
        case channelTypes.sms: {
          this.$set(this.currentMessage.sms, 'from', data.message.from)
          this.$set(this.currentMessage.sms, 'text', data.message.text)
          this.$set(
            this.currentMessage,
            'sms_unsubscribe',
            data.message.sms_unsubscribe
          )
          break
        }
        case channelTypes.facebook: {
          this.currentMessage.facebook.title = data.message.title
          this.currentMessage.facebook.subtitle = data.message.subtitle
          this.currentMessage.facebook.image_url = data.message.image_url
          if (data.message.default_action) {
            this.currentMessage.facebook.default_action.url =
              data.message.default_action.url
          }
          break
        }
        case channelTypes.inapp: {
          this.applyInapp(data)
          EventBus.$emit('applyPushTemplate-' + this.key, data.message)
          break
        }
        case channelTypes.webhook: {
          this.$set(
            this.currentMessage.webhook,
            'url',
            data.message.webhook.url
          )
          this.$set(
            this.currentMessage.webhook,
            'get_params',
            data.message.webhook.get_params !== ''
              ? data.message.webhook.get_params
              : []
          )
          this.$set(
            this.currentMessage.webhook,
            'request_body_type',
            data.message.webhook.request_body_type
          )
          this.$set(this.currentMessage.webhook, 'request_body_data', {})
          this.$set(
            this.currentMessage.webhook.request_body_data,
            'json',
            data.message.webhook.request_body_data.json !== ''
              ? data.message.webhook.request_body_data.json
              : []
          )
          this.$set(
            this.currentMessage.webhook.request_body_data,
            'post',
            data.message.webhook.request_body_data.post !== ''
              ? data.message.webhook.request_body_data.post
              : []
          )
          this.$set(
            this.currentMessage.webhook.request_body_data,
            'raw',
            data.message.webhook.request_body_data.raw
          )
          this.$set(
            this.currentMessage.webhook,
            'request_headers',
            data.message.webhook.request_headers !== ''
              ? data.message.webhook.request_headers
              : []
          )
          break
        }
        case channelTypes.whatsapp: {
          this.currentMessage.whatsapp.template_category =
            data.message.whatsapp.template_category
          this.currentMessage.whatsapp.template_name =
            data.message.whatsapp.template_name
          this.currentMessage.whatsapp.text = data.message.whatsapp.text
          this.currentMessage.whatsapp.params = {
            ...data.message.whatsapp.params
          }
          break
        }
        case channelTypes.onsite: {
          if (data.message.onsite) {
            this.$set(this.currentMessage, 'onsite', data.message.onsite)
          } else {
            let defaultOnsite = _.cloneDeep(
              defaultMessages[channelTypes.onsite].onsite
            )
            this.$set(this.currentMessage, 'onsite', defaultOnsite)
          }
          this.$set(
            this.currentMessage,
            'onsite_type',
            data.message.onsite_type
          )
          this.$set(
            this.currentMessage,
            'push_action',
            data.message.push_action
          )
          this.$set(this.currentMessage, 'push_icon', data.message.push_icon)
          this.$set(
            this.currentMessage,
            'push_silent',
            data.message.push_silent
          )
          this.$set(this.currentMessage, 'push_text', data.message.push_text)
          this.$set(this.currentMessage, 'push_title', data.message.push_title)
          EventBus.$emit('applyPushTemplate-' + this.key, data.message)
          break
        }
        case channelTypes.ios_push:
        case channelTypes.android_push:
        case channelTypes.web_push: {
          EventBus.$emit('applyPushTemplate-' + this.key, data.message)
        }
      }
      this.$set(this.currentMessage, 'template_context', {
        id: data.id,
        title: data.title,
        last_applied: new Date().getTime(),
        who_applied: this.$store.getters.user.email
      })
      this.applyTemplateMessageId = null
    },
    fetchOptOutAndShortenUrlData() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: '',
          params: {
            expand:
              'sms_opt_out,sms_opt_out_type,sms_opt_out_messages,sms_shorten_url,sms_shorten_url_domain'
          }
        })
      ).then(({ data }) => {
        this.optOutSettings = {
          enabled: data.sms_opt_out || false,
          type: data.sms_opt_out_type,
          messages: data.sms_opt_out_messages
        }
        this.shortenUrl = data.sms_shorten_url
        this.shortenUrlDomain = data.sms_shorten_url_domain
      })
    },
    extractText(emailTemplate) {
      let plainText = ''
      let parsedTemplateRows = JSON.parse(emailTemplate).page.rows
      parsedTemplateRows.forEach(row => {
        row.columns.forEach(col => {
          if (col.modules.length !== 0) {
            col.modules.forEach(module => {
              plainText += this.checkPropertyAndExtract(module, [
                'paragraph',
                'text',
                'html'
              ])
            })
          }
        })
      })
      // replace method is used to remove empty lines and whitespaces
      return plainText
        .trim()
        .replace(/\r?\n|\r|\t/g, ' ')
        .replace(/\s+/g, ' ')
    },
    checkPropertyAndExtract(object, properties) {
      let extractedText = ''
      for (const property of properties) {
        if (object.descriptor.hasOwnProperty(property)) {
          extractedText += this.extractTextFromHtml(
            object.descriptor[property].html
          )
        }
      }
      return extractedText
    },
    extractTextFromHtml(html) {
      let tempDivElement = document.createElement('div')
      tempDivElement.innerHTML = html
      let text = tempDivElement.textContent || tempDivElement.innerText || ''
      return text
        .replace(/\r?\n|\r|\t/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    dataFetchStopped() {
      EventBus.$emit('fetchingData', false)
    }
  },
  computed: {
    channels() {
      return {
        ...channelLabels,
        inapp: 'CampaignForm_ChannelsSelect_Inapp',
        workflow: 'CampaignForm_ChannelsSelect_Workflow'
      }
    },
    component() {
      switch (this.$route.query.channel) {
        case 'ios_push':
          return IosPush
        case 'android_push':
          return AndroidPush
        case 'web_push':
          return WebPush
        case 'email':
          return Email
        case 'sms':
          return SMS
        case 'inbox':
          return Inbox
        case 'inapp':
          return InApp
        case 'onsite':
          return OnSite
        case 'facebook':
          return Facebook
        case 'whatsapp':
          return Whatsapp
        case 'webhook':
          return Webhook
        case 'workflow':
          return Workflow
      }
    },
    key() {
      return `${this.$route.query.channel}_${this.$route.query.variant}_${this.$route.query.lang}`
    },
    channel() {
      return channelTypes[this.$route.query.channel]
    },
    lang() {
      return this.$route.query.lang === 'default'
        ? this.value.languages_default
        : this.$route.query.lang
    },
    variant() {
      return this.$route.query.variant === 'default'
        ? 'A'
        : this.$route.query.variant
    },
    currentMessage() {
      let path = `${this.channel}.${this.variant}.content`
      if (this.value.languages_multi) {
        path = `${this.channel}.${this.variant}.languages.${this.lang}`
      }
      if (!_.get(this.messages, path, null)) {
        return null
      }

      if (this.value.languages_multi) {
        return this.messages[this.channel][this.variant]['languages'][this.lang]
      } else {
        return this.messages[this.channel][this.variant]['content']
      }
    },
    title() {
      let title = this.$t(this.channels[this.$route.query.channel])
      if (
        this.$route.query.variant &&
        this.$route.query.variant !== 'default'
      ) {
        title += ` / ${this.$t('CampaignForm_Common_Title_Variant', {
          variant: this.$route.query.variant
        })}`
      }
      if (this.$route.query.lang && this.$route.query.lang !== 'default') {
        title += ` / ${languagesList[this.$route.query.lang]}`
      }
      return title
    },
    messages() {
      return this.value.messages
    },
    bindValues() {
      return {
        multilang: !!this.value.languages_multi,
        lang: this.lang,
        variant: this.variant,
        'opt-out-settings': this.optOutSettings,
        'shorten-url': !!this.shortenUrl,
        'shorten-url-domain': this.shortenUrlDomain,
        new_mxa: this.value.new_mxa
      }
    }
  },
  watch: {
    '$route.query.channel': function(newValue) {
      if (
        newValue === 'sms' &&
        !this.optOutSettings.hasOwnProperty('messages')
      ) {
        this.fetchOptOutAndShortenUrlData()
      }
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/CampaignContentPage.vue